import { ThemeProvider } from "@mui/material/styles";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { NavBar } from "./components/navbar";
import { IndexRouter } from "./setup/routes/routes";
import { ScrollToTop } from "./components/common/scrollToTop";
import { setAppToken } from "./setup/context/api";
import theme from "./styles/common/theme/theme";
import { useCallback, useEffect, useState } from "react";
import { AuthConfig } from "./setup/context/config/authConfig";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "./firebase/firebase";
import Sidebar from "./pages/business/sidebar";
import { isLoggedIn } from "./setup/context/authContext";
import "./App.css";
import AlertSystem from "./components/common/AlertSystem/AlertSystem";
import { ROUTES } from "./setup/constants";

// Create rtl cache
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

const hideSidebarRoutes = [
  ROUTES.CREATE_COMPLEX_CONTRACT.replace(":milestoneId?", "").replace(
    ":complexId?/",
    ""
  ),
  ROUTES.CONTRACT,
  ROUTES.SETTLEMENTS,
];
const whiteBackgroundRoutes = [
  ROUTES.CREATE_COMPLEX_CONTRACT.replace(":milestoneId?", "").replace(
    ":complexId?/",
    ""
  ),
  ROUTES.CONTRACT,
  ROUTES.SETTLEMENTS,
];

export const App = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTokenReady, setIsTokenReady] = useState(false);
  const [adminOrgEnabled, setAdminOrgEnabled] = useState<boolean>();

  useEffect(() => {
    const fetchRemoteConfig = async () => {
      try {
        await fetchAndActivate(remoteConfig);
        const adminOrgEnabledRemote = getValue(
          remoteConfig,
          "admin_org_enabled"
        ).asBoolean();
        setAdminOrgEnabled(adminOrgEnabledRemote);
      } catch (error) {
        console.error("Error fetching remote config:", error);
        setAdminOrgEnabled(false); // fallback
      }
    };
    fetchRemoteConfig();
  }, []);

  const decodeToken = useCallback((token) => {
    try {
      const base64Url = token.split(".")[1]; // Get the payload part of the token
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      const data = JSON.parse(jsonPayload);
      const notAnAdmin = data?.authorities?.some(
        (role) => role === "CLIENT_ADMIN"
      );
      setIsAdmin(!notAnAdmin);
    } catch (error) {
      // Fail safe: treat as non-admin on decode failure
      setIsAdmin(false);
    }
  }, []);

  // Wait for token to be available, then decode it
  useEffect(() => {
    const checkToken = () => {
      const userToken = AuthConfig.getToken();
      if (userToken) {
        decodeToken(userToken);
        setIsTokenReady(true);
      } else {
        // If no token, treat as non-admin for layout (fail safe)
        setIsAdmin(false);
        setIsTokenReady(true);
      }
    };

    // Check immediately
    checkToken();

    // Also check after a short delay in case token is being set asynchronously
    const timeoutId = setTimeout(checkToken, 100);

    return () => clearTimeout(timeoutId);
  }, [decodeToken]);

  function AppContent() {
    // Don't render anything until token is ready to prevent wrong page display
    if (!isTokenReady) {
      return (
        <CacheProvider value={cacheRtl}>
          <ThemeProvider theme={theme}>
            <div dir="rtl">
              <ScrollToTop path="/">
                {/* Loading state while determining role */}
                <div />
              </ScrollToTop>
              <AlertSystem />
            </div>
          </ThemeProvider>
        </CacheProvider>
      );
    }

    function SidebarContainer() {
      const location = useLocation();
      const isHideSidebar = hideSidebarRoutes.some((route) =>
        location?.pathname?.includes(route)
      );
      if (isHideSidebar) return null;
      return !isAdmin && isLoggedIn() ? <Sidebar /> : null;
    }

    function MainContainer() {
      const location = useLocation();
      const isWhiteBackground = whiteBackgroundRoutes.some((route) =>
        location?.pathname?.includes(route)
      );
      const clientAdminStyle = {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        background: isWhiteBackground ? "white" : "",
      };

      return (
        <div style={isAdmin || !isLoggedIn() ? {} : { ...clientAdminStyle }}>
          <SidebarContainer />
          <div
            className={
              isAdmin || !isLoggedIn() ? undefined : "outletContainer"
            }>
            <IndexRouter isAdmin={isAdmin} adminOrgEnabled={adminOrgEnabled} />
          </div>
        </div>
      );
    }

    return (
      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          <div dir="rtl">
            <ScrollToTop path="/">
              <Router>
                {isAdmin && (
                  <NavBar isAdmin={isAdmin} adminOrgEnabled={adminOrgEnabled} />
                )}
                <MainContainer />
              </Router>
            </ScrollToTop>
            <AlertSystem />
          </div>
        </ThemeProvider>
      </CacheProvider>
    );
  }

  return <AppContent />;
};
setAppToken();
